/**
 * Autolink for a URL that ends a sentence: `see ifp.iridium.com.` followed
 * by a space links `ifp.iridium.com` and leaves the full stop as text.
 *
 * The upstream `autolink` plugin only links the last word before the typed
 * whitespace when that WHOLE word tokenizes as one link (or one link in
 * brackets). `ifp.iridium.com.` tokenizes as a link plus a `.` symbol, so it
 * is rejected outright and the user has to type the space before the
 * punctuation for the link to form. Google Docs, Slack and Notion all link
 * the URL and leave the punctuation out; this plugin does the same.
 *
 * It runs only where upstream declined: the word must end in sentence
 * punctuation, the word minus that punctuation must be one link, and the
 * range must not already carry a link mark (upstream linked it) or a code
 * mark (code is never linked). The new mark covers the URL only, so the
 * revalidation plugin, which runs after this one, finds it already
 * normalised: linkify's own `find` drops the same trailing punctuation.
 */

import type { MarkType } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import {
  combineTransactionSteps,
  findChildrenInRange,
  getChangedRanges,
  getMarksBetween,
} from '@tiptap/react'
import { tokenize } from 'linkifyjs'

export interface LinkAutolinkPunctuationOptions {
  /** The `link` mark type of the schema this plugin runs against. */
  type: MarkType
  /** Scheme linkify assumes for scheme-less domains; must match the Link extension's. */
  defaultProtocol: string
  /** The Link extension's own URL allow-check, so this never links what upstream would refuse. */
  validate: (url: string) => boolean
  shouldAutoLink: (url: string) => boolean
}

export const linkAutolinkPunctuationPluginKey = new PluginKey('linkAutolinkPunctuation')

// The same whitespace class the upstream autolink plugin splits words on.
const WHITESPACE = '[\\u0000-\\u0020\\u00A0\\u1680\\u180E\\u2000-\\u2029\\u205F\\u3000]'
const WHITESPACE_END_RE = new RegExp(`${WHITESPACE}$`)
const WHITESPACE_SPLIT_RE = new RegExp(WHITESPACE)

/** Punctuation that ends a sentence or clause, optionally followed by closing quotes. */
const TRAILING_PUNCTUATION_RE = /[.,;:!?]+["'\u2019\u201D]*$/

export function linkAutolinkPunctuation(options: LinkAutolinkPunctuationOptions): Plugin {
  const { type, defaultProtocol, validate, shouldAutoLink } = options

  return new Plugin({
    key: linkAutolinkPunctuationPluginKey,
    appendTransaction(transactions, oldState, newState) {
      const docChanged = transactions.some((tr) => tr.docChanged) && !oldState.doc.eq(newState.doc)
      if (!docChanged) return null
      if (transactions.some((tr) => tr.getMeta('preventAutolink'))) return null

      const { tr } = newState
      const transform = combineTransactionSteps(oldState.doc, [...transactions])

      for (const { newRange } of getChangedRanges(transform)) {
        const blocks = findChildrenInRange(newState.doc, newRange, (n) => n.isTextblock)
        const first = blocks[0]
        if (!first) continue

        // Same two shapes upstream handles: Enter splits the block (the
        // first block's whole text ends the word), or a typed whitespace
        // character ends it inside one block.
        let textBefore: string
        if (blocks.length > 1) {
          textBefore = newState.doc.textBetween(
            first.pos,
            first.pos + first.node.nodeSize,
            undefined,
            ' ',
          )
        } else {
          const endText = newState.doc.textBetween(newRange.from, newRange.to, ' ', ' ')
          if (!WHITESPACE_END_RE.test(endText)) continue
          textBefore = newState.doc.textBetween(first.pos, newRange.to, undefined, ' ')
        }

        const words = textBefore.split(WHITESPACE_SPLIT_RE).filter(Boolean)
        const word = words[words.length - 1]
        if (!word) continue

        const punctuation = word.match(TRAILING_PUNCTUATION_RE)?.[0]
        if (!punctuation) continue
        const candidate = word.slice(0, word.length - punctuation.length)
        if (!candidate) continue

        const tokens = tokenize(candidate).map((t) => t.toObject(defaultProtocol))
        const link = tokens.length === 1 ? tokens[0] : undefined
        if (!link?.isLink || link.type !== 'url') continue
        if (!validate(link.value) || !shouldAutoLink(link.value)) continue

        // `textBetween` from a block's position counts the block's opening
        // token as a leading character, hence the +1 upstream uses too.
        const wordOffset = first.pos + textBefore.lastIndexOf(word)
        const from = wordOffset + link.start + 1
        const to = wordOffset + link.end + 1

        const code = newState.schema.marks.code
        if (code && newState.doc.rangeHasMark(from, to, code)) continue
        if (getMarksBetween(from, to, newState.doc).some((m) => m.mark.type === type)) continue

        tr.addMark(from, to, type.create({ href: link.href }))
      }

      return tr.steps.length ? tr : null
    },
  })
}
